import React, { useState } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import { ReactComponent as ModalCloseIcon } from "../../../assets/svg/close.svg";
import { ReactComponent as CalendarIcon } from "../../../assets/svg/new_calendar_icon.svg";
import { ReactComponent as ArrowDownIcon } from "../../../assets/svg/mark_down.svg";
import "./manual-modals.css";

import { saveVehicleApi } from "../../visitor/visitor-api.js";

// Constants localized to this file
const STATUS_OPTIONS = ["Active", "Expired"];
const PERMIT_TYPE_OPTIONS = ["National", "State"];

// Internal Reusable Components
const FormField = ({ label, required, children }) => (
  <Col md={3}>
    <label className="vm-label">
      {label}
      {required && <span className="vm-required">*</span>}
    </label>
    {children}
  </Col>
);

const TextInput = ({ placeholder = "Enter here...", value, onChange }) => <input type="text" placeholder={placeholder} className="vm-input" value={value} onChange={(e) => onChange(e.target.value)} />;

const DateInput = ({ placeholder = "dd/mm/yyyy", value, onChange }) => (
  <div className="vm-input-wrapper">
    <input type="date" placeholder={placeholder} className="vm-input" value={value} onChange={(e) => onChange(e.target.value)} onClick={(e) => e.target.showPicker?.()} />
    <CalendarIcon className="vm-icon-calendar" width="18" height="18" />
  </div>
);

const SelectInput = ({ value, onChange, options = [], placeholder = "Select" }) => (
  <div className="vm-input-wrapper">
    <select className="vm-input vm-select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
    <ArrowDownIcon className="vm-icon-arrow" width="24" height="24" />
  </div>
);

const ManualVehicleModal = ({ onHide, onSuccess, onCancel, onSubmit, vehicleNo = "TN02 RG 4506", show }) => {
  const close = onHide || onCancel;
  const success = onSuccess || onSubmit;
  const [rcData, setRcData] = useState({ number: "", startDate: "", expiryDate: "", status: "Active" });
  const [insuranceData, setInsuranceData] = useState({ company: "", startDate: "", expiryDate: "", status: "Active" });
  const [permitData, setPermitData] = useState({ number: "", startDate: "", expiryDate: "", type: "State", status: "Active" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  if (!show) return null;

  const handleCancel = () => {
    if (close) close();
  };

  const handleSubmit = async () => {
    // Basic validation for required fields
    const newErrors = {};
    if (!rcData.expiryDate) newErrors.rc_expiry = "Required";
    if (!insuranceData.expiryDate) newErrors.insurance_expiry = "Required";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        vehicle_number: vehicleNo,
        rc_number: rcData.number || vehicleNo, // Fallback if empty
        rc_valid_upto: rcData.expiryDate,
        rc_status: rcData.status,
        insurance_company: insuranceData.company,
        insurance_upto: insuranceData.expiryDate,
        insurance_status: insuranceData.status,
        permit_number: permitData.number || "N/A",
        permit_valid_upto: permitData.expiryDate || rcData.expiryDate, // Fallback
        permit_status: permitData.status,
      };

      const response = await saveVehicleApi(payload);
      if (response && response.success) {
        if (success) {
          success(response.vehicle_data);
        }
      } else {
        alert(response?.message || "Failed to save vehicle details");
      }
    } catch (err) {
      console.error("Error saving vehicle:", err);
      alert("An error occurred while saving.");
    } finally {
      setLoading(false);
    }
  };

  const updateData = (setter, key, value) => {
    setter((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="vm-overlay">
      <div className="vm-container">
        <div className="vm-header">
          <div className="vm-header-left">
            <h2 className="vm-title">Vehicle details</h2>
          </div>
          <div className="vm-header-right">
            <div className="vm-badge">
              <span className="vm-badge-label">Vehicle No : </span>
              <span className="vm-badge-value">{vehicleNo}</span>
            </div>
            <div className="vm-close-circle" onClick={handleCancel}>
              <ModalCloseIcon width="14" height="14" />
            </div>
          </div>
        </div>

        <div className="vm-body">
          {/* RC Details Section */}
          <div className="vm-section">
            <h3 className="vm-section-title">RC details</h3>
            <Row className="gx-3 gy-3">
              <FormField label="RC Number">
                <TextInput value={rcData.number} onChange={(val) => updateData(setRcData, "number", val)} />
              </FormField>
              <FormField label="Start date">
                <DateInput value={rcData.startDate} onChange={(val) => updateData(setRcData, "startDate", val)} />
              </FormField>
              <FormField label="Expiry date" required>
                <DateInput value={rcData.expiryDate} onChange={(val) => updateData(setRcData, "expiryDate", val)} />
                {errors.rc_expiry && <span className="vm-error-msg">{errors.rc_expiry}</span>}
              </FormField>
              <FormField label="Status">
                <SelectInput value={rcData.status} options={STATUS_OPTIONS} onChange={(val) => updateData(setRcData, "status", val)} />
              </FormField>
            </Row>
          </div>

          {/* Insurance Details Section */}
          <div className="vm-section">
            <h3 className="vm-section-title">Insurance details</h3>
            <Row className="gx-3 gy-3">
              <FormField label="Insurance Company">
                <TextInput value={insuranceData.company} onChange={(val) => updateData(setInsuranceData, "company", val)} />
              </FormField>
              <FormField label="Start date">
                <DateInput value={insuranceData.startDate} onChange={(val) => updateData(setInsuranceData, "startDate", val)} />
              </FormField>
              <FormField label="Expiry date" required>
                <DateInput value={insuranceData.expiryDate} onChange={(val) => updateData(setInsuranceData, "expiryDate", val)} />
                {errors.insurance_expiry && <span className="vm-error-msg">{errors.insurance_expiry}</span>}
              </FormField>
              <FormField label="Status">
                <SelectInput value={insuranceData.status} options={STATUS_OPTIONS} onChange={(val) => updateData(setInsuranceData, "status", val)} />
              </FormField>
            </Row>
          </div>

          {/* Permit Details Section */}
          <div className="vm-section">
            <h3 className="vm-section-title">Permit details</h3>
            <Row className="gx-3 gy-3">
              <FormField label="Permit Number">
                <TextInput value={permitData.number} onChange={(val) => updateData(setPermitData, "number", val)} />
              </FormField>
              <FormField label="Start date">
                <DateInput value={permitData.startDate} onChange={(val) => updateData(setPermitData, "startDate", val)} />
              </FormField>
              <FormField label="Expiry date">
                <DateInput value={permitData.expiryDate} onChange={(val) => updateData(setPermitData, "expiryDate", val)} />
              </FormField>
              <FormField label="Permit Type">
                <SelectInput value={permitData.type} options={PERMIT_TYPE_OPTIONS} onChange={(val) => updateData(setPermitData, "type", val)} />
              </FormField>
            </Row>
          </div>
        </div>

        <div className="vm-footer">
          <button className="vm-btn-cancel" onClick={handleCancel} disabled={loading}>
            Cancel
          </button>
          <button className="vm-btn-submit" onClick={handleSubmit} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" style={{ color: "#ffffff" }} /> : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualVehicleModal;
